import React, { useState } from 'react';
import { dpsTheme } from '../../../styles/dpsTheme';

interface ActionCompletionModalProps {
  isOpen: boolean;
  actionTitle: string;
  onClose: () => void;
  onConfirm: (actualCost: number, notes: string) => void;
}

export const ActionCompletionModal: React.FC<ActionCompletionModalProps> = ({
  isOpen,
  actionTitle,
  onClose,
  onConfirm,
}) => {
  const [actualCost, setActualCost] = useState('');
  const [notes, setNotes] = useState('');

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm(Number(actualCost), notes);
    setActualCost('');
    setNotes('');
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(4px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        className="backdrop-blur-xl border border-gray-700/50 rounded-lg p-4 sm:p-6 lg:p-8 max-w-lg w-[90%] max-h-[90vh] overflow-y-auto"
        style={{ background: 'rgba(30, 41, 59, 0.95)' }}
      >
        <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{
              background: dpsTheme.colors.status.completed.bg,
              border: `1px solid ${dpsTheme.colors.status.completed.border}`,
              color: dpsTheme.colors.status.completed.text,
            }}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h3 className="text-xl sm:text-2xl font-semibold text-white">
            Confirmer la réalisation
          </h3>
        </div>

        <p className="text-sm sm:text-base font-semibold text-white mb-4 sm:mb-6">
          {actionTitle}
        </p>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: dpsTheme.spacing.xl }}>
            <label
              style={{
                display: 'block',
                fontSize: dpsTheme.typography.fontSize.sm,
                fontWeight: dpsTheme.typography.fontWeight.medium,
                color: dpsTheme.colors.text.tertiary,
                marginBottom: '8px',
              }}
            >
              Coût réel en euros *
            </label>
            <input
              type="number"
              value={actualCost}
              onChange={(e) => setActualCost(e.target.value)}
              placeholder="312"
              required
              style={{
                width: '100%',
                background: dpsTheme.colors.background.darker,
                border: `1px solid ${dpsTheme.colors.border.primary}`,
                borderRadius: '8px',
                padding: dpsTheme.spacing.md,
                color: dpsTheme.colors.text.primary,
                fontSize: dpsTheme.typography.fontSize.base,
                fontFamily: dpsTheme.typography.fontFamily,
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.5)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = dpsTheme.colors.border.primary;
              }}
            />
          </div>

          <div style={{ marginBottom: dpsTheme.spacing.xl }}>
            <label
              style={{
                display: 'block',
                fontSize: dpsTheme.typography.fontSize.sm,
                fontWeight: dpsTheme.typography.fontWeight.medium,
                color: dpsTheme.colors.text.tertiary,
                marginBottom: '8px',
              }}
            >
              Notes d'implémentation (optionnel)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Installation réalisée par SecurHome le 6 novembre 2025."
              style={{
                width: '100%',
                minHeight: '100px',
                background: dpsTheme.colors.background.darker,
                border: `1px solid ${dpsTheme.colors.border.primary}`,
                borderRadius: '8px',
                padding: dpsTheme.spacing.md,
                color: dpsTheme.colors.text.primary,
                fontSize: dpsTheme.typography.fontSize.base,
                fontFamily: dpsTheme.typography.fontFamily,
                resize: 'vertical',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.5)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = dpsTheme.colors.border.primary;
              }}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-end mt-4 sm:mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 sm:px-5 sm:py-2 rounded-lg font-semibold text-sm sm:text-base cursor-pointer transition-all border-0 hover:bg-white/10"
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                color: dpsTheme.colors.text.muted,
              }}
            >
              Annuler
            </button>

            <button
              type="submit"
              className="px-4 py-2.5 sm:px-5 sm:py-2 rounded-lg font-semibold text-sm sm:text-base cursor-pointer transition-all border-0 hover:-translate-y-0.5 hover:shadow-lg"
              style={{
                background: dpsTheme.colors.accent.green.gradient,
                color: 'white',
              }}
            >
              Confirmer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
